import React, { useState, useEffect } from 'react';
import { callOpenAI } from '../services/openai';
import './SessionView.css';
import TranscriptView from './TranscriptView';
import { CustomPrompt } from '../types/prompts';
import { BlobServiceClient } from '@azure/storage-blob';

interface SessionViewProps {
    selectedSessions: string[];
}

const SessionView: React.FC<SessionViewProps> = ({ selectedSessions }) => {
    const [selectedPromptId, setSelectedPromptId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [aiResponse, setAiResponse] = useState<string>('');
    const [customPrompts, setCustomPrompts] = useState<CustomPrompt[]>([]);
    const [sessionContents, setSessionContents] = useState<{[key: string]: string}>({});
    
    const getCookieValue = (name: string): string => {
        const cookie = document.cookie
            .split('; ')
            .find(row => row.startsWith(`${name}=`));
        return cookie ? cookie.split('=')[1] : '';
    };

    const fetchSessionContent = async (sessionId: string): Promise<string> => {
        const accountName = getCookieValue('storageAccountName');
        const containerName = getCookieValue('containerName');
        
        const blobServiceClient = new BlobServiceClient(
            `https://${accountName}.blob.core.windows.net`
        );
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const blobClient = containerClient.getBlobClient(sessionId);
        
        const downloadResponse = await blobClient.download();
        const downloaded = await downloadResponse.blobBody;
        if (!downloaded) return '';
        
        const content = await new Response(downloaded).text();
        return content;
    };

    useEffect(() => {
        const savedPrompts = getCookieValue('customPrompts');
        if (savedPrompts) {
            try {
                setCustomPrompts(JSON.parse(decodeURIComponent(savedPrompts)));
            } catch (e) {
                console.error('Failed to parse custom prompts:', e);
            }
        }
    }, []);

    useEffect(() => {
        const fetchContents = async () => {
            // Fetch contents for all selected sessions
            const contents: {[key: string]: string} = {};
            for (const sessionId of selectedSessions) {
                const content = await fetchSessionContent(sessionId);
                contents[sessionId] = content;
            }
            setSessionContents(contents);
        };
        
        if (selectedSessions.length > 0) {
            fetchContents();
        }
    }, [selectedSessions]);

    // Add this effect to clear AI response when selections change
    useEffect(() => {
        setAiResponse('');
    }, [selectedSessions]);

    const handlePrompt = async (promptType: 'summarize' | 'quotes' | 'custom') => {
        if (selectedSessions.length === 0) return;

        setIsLoading(true);
        try {
            // Get the appropriate prompt based on type
            let prompt = '';
            if (promptType === 'custom') {
                const selectedPrompt = customPrompts.find(p => p.id === selectedPromptId);
                if (!selectedPrompt) return;
                prompt = selectedPrompt.prompt;
            } else {
                prompt = getCookieValue(`${promptType}Prompt`);
            }

            // Combine all session contents with clear separation
            const combinedContent = selectedSessions
                .map(sessionId => {
                    const filename = sessionId.split('/').pop() || sessionId;
                    return `=== Session: ${filename} ===\n${sessionContents[sessionId]}\n\n`;
                })
                .join('\n');

            const response = await callOpenAI(
                prompt,
                combinedContent,
                getCookieValue('chatGPTKey')
            );
            setAiResponse(response);
        } catch (error) {
            console.error('AI Analysis failed:', error);
            setAiResponse('Failed to analyze transcripts.');
        }
        setIsLoading(false);
    };

    const getBlobUrl = (sessionId: string) => {
        const storageAccountName = getCookieValue('storageAccountName');
        const containerName = getCookieValue('containerName');
        return `https://${storageAccountName}.blob.core.windows.net/${containerName}/${sessionId}`;
    };

    return (
        <div className="session-view">
            {selectedSessions.length > 0 ? (
                <>
                    <div className="selected-sessions">
                        {selectedSessions.map(sessionId => (
                            <div key={sessionId} className="session-card">
                                <span className="session-name">{sessionId.split('/').pop()}</span>
                                <a 
                                    href={getBlobUrl(sessionId)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="open-icon"
                                    title="Open in new tab"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                    </svg>
                                </a>
                            </div>
                        ))}
                    </div>
                    <div className="session-header">
                        <div className="ai-actions">
                            <button onClick={() => handlePrompt('summarize')}>
                                Summarize Session
                            </button>
                            <button onClick={() => handlePrompt('quotes')}>
                                Get Quotes
                            </button>
                            <div className="custom-prompt-selector">
                                <select 
                                    value={selectedPromptId}
                                    onChange={(e) => setSelectedPromptId(e.target.value)}
                                    className="prompt-select"
                                >
                                    <option value="">Select Custom Prompt</option>
                                    {customPrompts.map(prompt => (
                                        <option key={prompt.id} value={prompt.id}>
                                            {prompt.name}
                                        </option>
                                    ))}
                                </select>
                                <button onClick={() => handlePrompt('custom')} disabled={!selectedPromptId}>
                                    Run Custom Prompt
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {isLoading && <div className="loading">Analyzing transcript...</div>}
                    
                    {aiResponse && (
                        <div className="ai-response">
                            <h3>AI Analysis</h3>
                            <div className="response-content">{aiResponse}</div>
                        </div>
                    )}

                    <TranscriptView content={sessionContents} />
                </>
            ) : (
                <div className="empty-state">Select sessions to analyze</div>
            )}
        </div>
    );
};

export default SessionView;
